import { ERole } from '@dlap-ui/core';

export interface ApplicationServiceCatalogItem {
  name: string;
  value: string;
  aliases?: string[];
  icon: string;
  description: string;
  supportedInWorkflow: boolean;
  allowedRoles?: readonly string[];
}

export type ServiceOption = ApplicationServiceCatalogItem;

export const APPLICATION_SERVICE_CATALOG: ApplicationServiceCatalogItem[] = [
  {
    name: 'Deed of Transfer',
    value: 'deed-transfer',
    aliases: ['deed-transfer-bond', 'deed-transfer-ordinary'],
    icon: 'swap',
    description: 'Transfer ownership of a registered property from one party to another.',
    // Served by its own step-by-step page (/overview/apply/transfer), not the
    // metadata-driven workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Subdivision/Deduction',
    value: 'deed-subdivision',
    aliases: ['deed-subdivision-bond'],
    icon: 'branches',
    description: 'Divide a single property into smaller, separately registrable portions.',
    // Served by its own step-by-step page (/overview/apply/subdivision), not
    // the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Consolidation',
    value: 'deed-consolidation',
    icon: 'cluster',
    description: 'Combine two or more adjoining properties into a single registered title.',
    // Served by its own step-by-step page (/overview/apply/consolidation), not
    // the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Deed of Grant',
    value: 'deed-of-grant',
    aliases: ['deed-of-grant-bond'],
    icon: 'file-protect',
    description: 'A legal document that serves as the title deed for land transferred directly from the state to an individual or entity. It creates a new title, establishing the first private owner of a piece of land that was previously unalienated state land. It is used when the state grants or gives away a piece of land, rather than when land is bought or sold between private parties.',
    supportedInWorkflow: true,
    allowedRoles: [ERole.LOCAL_GOVERNMENT_TECHNICIAN],
  },
  {
    name: 'Land Tenure Deed of Grant',
    value: 'land-tenure-deed-of-grant',
    aliases: ['land-tenure-deed-of-grant-bond'],
    icon: 'file-protect',
    description: 'A Land Tenure Transfer is a Deed of Grant used for transferring unalienated land from the state. Draft deeds are lodged by the Minister and are handled in a manner similar to a subdivisional transfer.',
    supportedInWorkflow: true,
    allowedRoles: [ERole.MINISTER_OF_LANDS_TECHNICIAN],
  },
  {
    name: 'Deed Donation Transfer',
    value: 'deed-donation-transfer',
    aliases: ['deed-donation-transfer-bond'],
    icon: 'gift',
    description: 'Transfer property by way of donation with donor, donee, and attachment details.',
    supportedInWorkflow: true,
  },
  {
    name: 'Caveat Registration',
    value: 'caveat-registration',
    icon: 'alert',
    description: 'Register a caveat against a deed with plaintiffs, defendants, and court attachments.',
    supportedInWorkflow: true,
  },
  {
    name: 'Caveat Upliftment',
    value: 'caveat-upliftment',
    icon: 'disconnect',
    description: 'Lift an existing caveat using deed details, office selection, and scanned supporting documents.',
    supportedInWorkflow: true,
  },
  {
    name: 'Sectional Title',
    value: 'sectional-title',
    aliases: ['land-tenure-sectional-title'],
    icon: 'home',
    description: 'Register individual ownership of a unit within a sectional title scheme.',
    // Served by its own step-by-step page (/overview/apply/sectional-title),
    // not the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Land Tenure Transfer',
    value: 'land-tenure-transfer',
    aliases: ['land-tenure-transfer-bond'],
    icon: 'environment',
    description: 'Transfer rights over communal or leasehold land tenure.',
    // Served by its own step-by-step page
    // (/overview/apply/land-tenure-transfer), not the metadata-driven
    // workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Land Tenure Subdivision',
    value: 'land-tenure-subdivision',
    aliases: ['land-tenure-subdivision-bond'],
    icon: 'environment',
    description: 'Subdivide land held under tenure into smaller allotments.',
    // Served by its own step-by-step page
    // (/overview/apply/land-tenure-subdivision), not the metadata-driven
    // workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Bond Registration',
    value: 'bond-registration',
    icon: 'safety-certificate',
    description: 'Register a bond against a property with mortgagee and mortgagor details.',
    supportedInWorkflow: true,
  },
  {
    name: 'Bond Cancellation',
    value: 'bond-cancellation',
    icon: 'close-circle',
    description: 'Cancel an existing bond against a registered property.',
    supportedInWorkflow: true,
  },
  {
    name: 'Note of Hand',
    value: 'note-of-hand',
    icon: 'wallet',
    description: 'Capture note of hand security information with mortgagor and attachment details.',
    supportedInWorkflow: true,
  },
  {
    name: 'Issuance of a Securitized Title Deed',
    value: 'deed-re-issuance',
    aliases: ['deed-re-issuance-bond'],
    icon: 'safety-certificate',
    description: 'Replace an existing title deed with a new securitized document.',
    supportedInWorkflow: true,
  },
  {
    name: 'Lease Cancellation',
    value: 'lease-cancellation',
    icon: 'close-circle',
    description: 'Cancel a registered lease agreement on a property.',
    supportedInWorkflow: true,
  },
  {
    name: 'Estate',
    value: 'estate',
    aliases: ['deed-estate-transfer-bond'],
    icon: 'bank',
    description: 'Administer the transfer of property held within a deceased estate.',
    // Served by its own step-by-step page (/overview/apply/estate), not
    // the metadata-driven workflow — so it must not appear in that
    // page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Notarial Deed Section',
    value: 'notarial-deed-section',
    aliases: ['land-tenure-notarial-deed-section'],
    icon: 'file-protect',
    description:
      'Notarial Deed in terms of Section 27 of the Deeds Registries Act (20:05) — divide a property into undivided shares.',
    // Served by its own step-by-step page
    // (/overview/apply/notarial-deed-section), not the metadata-driven
    // workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Notarial Deed of Donation and Trust',
    value: 'trust-registration',
    icon: 'gift',
    description:
      'Trust Registration — register a trust with its objectives, founders, trustees and beneficiaries.',
    // Served by its own step-by-step page (/overview/apply/trust-registration),
    // not the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Rectification',
    value: 'rectification',
    icon: 'form',
    description: 'Rectify deed information using the shared application workflow structure.',
    supportedInWorkflow: true,
  },
];

/**
 * The catalog name for a backend application-type code — e.g.
 * `DEED_RE_ISSUANCE` becomes `Issuance of a Securitized Title Deed`. The codes
 * are the SCREAMING_SNAKE form of the catalog's kebab-case keys, so they are
 * normalised before lookup. A code the catalog does not know is title-cased,
 * which keeps unrecognised drafts readable instead of showing a raw code.
 */
export function resolveServiceName(
  applicationType: string | null | undefined,
  fallback = 'Unknown service',
): string {
  const code = (applicationType ?? '').trim();
  if (!code) {
    return fallback;
  }

  const serviceKey = code.toLowerCase().replace(/_/g, '-');
  const match = APPLICATION_SERVICE_CATALOG.find(
    (item) => item.value === serviceKey || item.aliases?.includes(serviceKey),
  );

  if (match) {
    return match.name;
  }

  return code
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
